import { useMemo, useState } from "react";
import { motion } from "motion/react";
import Markdown, { defaultUrlTransform, type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { Button } from "@/components/ui/button";
import {
  useAgentChat,
  type AgentMessage,
  type AgentSuggestion,
  type ToolAction,
} from "@/components/AgentChatProvider";
import { linkRefs, REF_SCHEME } from "@/lib/agentRefs";

/** Messaging-app style "typing" dots, shown until the agent's first token or status arrives. */
function TypingIndicator() {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <span role="status" aria-label="Agent is typing" className="flex h-5 items-center gap-1">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          className="size-1.5 rounded-full bg-muted-foreground"
          animate={reducedMotion ? { opacity: 0.6 } : { y: [0, -4, 0], opacity: [0.35, 1, 0.35] }}
          transition={reducedMotion ? undefined : { duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }}
        />
      ))}
    </span>
  );
}

function StatusIndicator({ label }: { label: string }) {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <motion.span
      role="status"
      aria-live="polite"
      className="block text-sm text-muted-foreground"
      animate={reducedMotion ? { opacity: 1 } : { opacity: [0.45, 1, 0.45] }}
      transition={reducedMotion ? undefined : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
    >
      {label}...
    </motion.span>
  );
}

function ActionButtons({ actions }: { actions: ToolAction[] }) {
  const { onAction } = useAgentChat();
  if (actions.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {actions.map((action) => (
        <Button
          key={`${action.type}:${action.label}`}
          size="sm"
          variant="outline"
          onClick={() => onAction(action)}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );
}

// Where each ref type lives in the app; the click itself goes through the host's open_<type> action.
const REF_PATHS: Record<string, (id: string) => string> = {
  invoice: (id) => `/invoices/${id}`,
  bill: (id) => `/bills/${id}`,
  expense: (id) => `/expenses/${id}`,
  merchant: (id) => `/expenses/merchants/${id}`,
};

function RefLink({ href, children }: { href: string; children: React.ReactNode }) {
  const { onAction } = useAgentChat();
  const [type, id] = href.slice(REF_SCHEME.length).split("/").map(decodeURIComponent);
  return (
    <a
      href={(type && id && REF_PATHS[type]?.(id)) || "#"}
      className="font-medium text-primary underline-offset-2 hover:underline"
      onClick={(e) => {
        e.preventDefault();
        onAction({ type: `open_${type}`, label: String(children), params: { id } });
      }}
    >
      {children}
    </a>
  );
}

const markdownComponents: Components = {
  a: ({ href, children, ...rest }) =>
    href?.startsWith(REF_SCHEME) ? (
      <RefLink href={href}>{children}</RefLink>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    ),
};

// react-markdown drops unknown URL schemes; ours is internal and resolved by RefLink.
const urlTransform = (url: string) => (url.startsWith(REF_SCHEME) ? url : defaultUrlTransform(url));

function confirmQuestion(suggestion: AgentSuggestion) {
  const [, who, what] = suggestion.label.split(" · ");
  if (suggestion.tool === "send_invoice_reminder" && who && what) return `Send reminder to ${who} for ${what}?`;
  // "Approve · Acme · INV-001 · £120.00" → "Approve Acme INV-001 for £120.00?"
  const amount = suggestion.label.split(" · ")[3];
  if (suggestion.tool === "approve_bill" && who && what && amount) return `Approve ${who} ${what} for ${amount}?`;
  // Expense chips: "Approve · Pret A Manger · £4.50", "Retry post · … · … [· Xero]", "Accept as is · … · …".
  if (suggestion.tool === "approve_expense" && who && what) return `Approve ${who} ${what}?`;
  if (suggestion.tool === "retry_expense_post" && who && what)
    return `Retry posting ${who} ${what} to ${amount ?? "the accounting platform"}?`;
  if (suggestion.tool === "resolve_expense_flag" && who && what) return `Accept ${who} ${what} as it stands?`;
  return `${suggestion.label.replaceAll(" · ", ", ")}?`;
}

type ChipState = "idle" | "confirming" | "running" | "done" | "failed";

function SuggestionChip({ suggestion }: { suggestion: AgentSuggestion }) {
  const { runSuggestion, isStreaming } = useAgentChat();
  const [state, setState] = useState<ChipState>(suggestion.done ? "done" : "idle");
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setState("running");
    setError(null);
    const result = await runSuggestion(suggestion);
    if (result.error) {
      setError(result.error);
      setState("failed");
      toast.error(result.error);
    } else {
      setState("done");
    }
  };

  if (state === "confirming") {
    return (
      <div className="flex w-full flex-wrap items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
        <span className="text-sm">{confirmQuestion(suggestion)}</span>
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => setState("idle")}>
            Cancel
          </Button>
          <Button size="sm" onClick={run}>
            Confirm
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <Button
        size="sm"
        variant="outline"
        disabled={state === "running" || state === "done" || isStreaming}
        aria-busy={state === "running"}
        className={cn(
          "h-auto justify-start whitespace-normal py-1.5 text-left",
          state === "done" && "border-emerald-600/40 text-emerald-700 disabled:opacity-100 dark:text-emerald-400",
          state === "failed" && "border-destructive/50",
        )}
        onClick={() => (suggestion.confirm ? setState("confirming") : run())}
      >
        {state === "running" && <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />}
        {state === "done" && <Check className="size-3.5" aria-hidden="true" />}
        {state === "failed" && <X className="size-3.5 text-destructive" aria-hidden="true" />}
        <span>
          {suggestion.label}
          {suggestion.reason && state === "idle" && (
            <span className="text-muted-foreground"> - {suggestion.reason}</span>
          )}
          {state === "done" && <span className="text-muted-foreground"> - done</span>}
        </span>
      </Button>
      {state === "failed" && error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

function SuggestionChips({ suggestions }: { suggestions: AgentSuggestion[] }) {
  return (
    <div className="mt-2 flex flex-col items-start gap-1.5" aria-label="Suggested actions">
      {suggestions.map((s) => (
        // Keyed on done too, so a chip another reply ran remounts as done.
        <SuggestionChip key={`${s.id}:${s.done}`} suggestion={s} />
      ))}
    </div>
  );
}

export function AgentChatMessage({ message, isLatest = false }: { message: AgentMessage; isLatest?: boolean }) {
  const { status, isStreaming } = useAgentChat();
  const isUser = message.role === "user";
  const hasActions = !isUser && !!message.actions && message.actions.length > 0;
  const hasSuggestions = !isUser && !!message.suggestions && message.suggestions.length > 0;
  // The live status belongs to the reply being streamed, i.e. the latest assistant message.
  const liveStatus = !isUser && isLatest && isStreaming ? status : null;
  const content = useMemo(
    () => (isUser ? message.content : linkRefs(message.content, message.refs)),
    [isUser, message.content, message.refs],
  );

  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] rounded-lg px-3 py-2 text-sm overflow-x-auto",
          isUser ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
          !isUser &&
            "[&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:my-1 [&_li]:my-0.5 [&_a]:underline [&_table]:w-full [&_table]:my-2 [&_table]:border-collapse [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold [&_th]:bg-background/50 [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1",
        )}
      >
        {message.content ? (
          isUser ? (
            <span className="whitespace-pre-wrap">{message.content}</span>
          ) : (
            <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents} urlTransform={urlTransform}>
              {content}
            </Markdown>
          )
        ) : liveStatus ? null : (
          <TypingIndicator />
        )}
        {liveStatus && <StatusIndicator label={liveStatus} />}
        {hasActions && <ActionButtons actions={message.actions!} />}
        {hasSuggestions && <SuggestionChips suggestions={message.suggestions!} />}
      </div>
    </div>
  );
}
